/* eslint-disable max-len */
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ScullyLibModule } from '@scullyio/ng-lib';
import { CommonModule, DecimalPipe } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbTooltipModule, NgbModalModule, NgbActiveModal, NgbAccordionModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';

// Components
import { AboutDescriptionComponent } from './components/pages/about/about-description/about-description.component';
import { Ab4PoliciesComponent } from './components/pages/about/about-gard-website-faq/about-gard-website-faq.component';
import { AboutComponent } from './components/pages/about/about.component';
import { Co2ContactCenterComponent } from './components/pages/contact-us/contact-us-contact-center/contact-us-contact-center.component';
import { ContactResourcesComponent } from './components/pages/contact-us/contact-resources/contact-resources.component';
import { ContactUsComponent } from './components/pages/contact-us/contact-us.component';
import { EspanolDiseaseDetailComponent } from './components/disease-es/espanol-disease-detail.component';
import { AgeAtOnsetComponent } from './components/disease/disease-page/age-at-onset/age-at-onset.component';
import { CausesComponent } from './components/disease/disease-page/causes/causes.component';
import { CausesTabsComponent } from './components/disease/disease-page/causes-tabs/causes-tabs.component';
import { DiseaseAboutComponent } from './components/disease/disease-page/disease-about.component';
import { DiseaseAtAGlanceSummaryComponent } from './components/disease/disease-page/disease-at-a-glance-summary/disease-at-a-glance-summary.component';
import { DiseaseAtAGlanceComponent } from './components/disease/disease-page/disease-at-a-glance/disease-at-a-glance.component';
import { SymptomsSectionComponent } from './components/disease/disease-page/symptoms-section/symptoms-section.component';
import { YourMedicalTeamComponent } from './components/disease/disease-page/your-medical-team/your-medical-team.component';
import { PatientOrganizationsAccountsComponent } from './components/disease/disease-page/patient-organizations-accounts/patient-organizations-accounts.component';
import { GenericPageComponent } from './components/generic/generic.component';
import { H2AboutGardComponent } from './components/homepage/h2-about-gard/h2-about-gard.component';
import { H3UnderstandComponent } from './components/homepage/h3-understand/h3-understand.component';
import { H5ContactUsComponent } from './components/homepage/h5-contact-us/h5-contact-us.component';
import { HomepageComponent } from './components/pages/home-page/homepage.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { BottomSourcesDateInformationComponent } from './components/common/bottom-sources-date-information/bottom-sources-date-information.component';
import { ClampComponent } from './components/common/clamp/clamp.component';
import { FooterComponent } from './components/common/footer/footer.component';
import { MainBannerComponent } from './components/common/main-banner/main-banner.component';
import { MobileSearchItemComponent } from './components/common/mobile-search-item/mobile-search-item.component';
import { MobilePanelListComponent } from './components/common/mobile-panel-list/mobile-panel-list.component';
import { ModalBaseComponent } from './components/common/modal-base/modal-base.component';
import { ScrollToTopComponent } from './components/common/scroll-to-top/scroll-to-top.component';
import { TooltipComponent } from './components/common/tooltip/tooltip.component';
import { TopNavigationComponent } from './components/common/top-navigation/top-navigation.component';
import { ViewSourcesReferencesComponent } from './components/common/view-sources-references/view-sources-references.component';
import { SearchResultPageComponent } from './components/search-result-page/search-result-page.component';
import { EmbeddedHtmlDirective } from './directives/embedded-html/embedded-html.directive';
import { ExternalLinkDirective } from './directives/external-link/external-link.directive';
import { MatchHeightDirective } from './directives/match-height/match-height.directive';
import { NumberOrStringToDecimalPipe } from './pipes/number-or-string-to-decimal.pipe';
import { NumberRoundingSuffixPipe } from './pipes/number-rounding-suffix.pipe';
import { SafeHTMLPipe } from './pipes/safe-html.pipe';
import { TitleCaseWordPipe } from './pipes/title-case-word.pipe';
import { ErrorListComponent } from './testing/error-list/error-list.component';
import { ContactUsEsComponent } from './components/pages/contact-us/contact-us-es/contact-us-es.component';
import { BannerFeedbackComponent } from './components/common/banner-feedback/banner-feedback.component';
import { DiseaseCasePipe } from './pipes/disease-case.pipe';
import { AutoFocusDirective } from './directives/auto-focus/auto-focus.directive';
import { FilterByLetterComponent } from './components/search-result-page/filter-by-letter/filter-by-letter.component';
import { FilterBubblesComponent } from './components/search-result-page/filter-bubbles/filter-bubbles.component';
import { FilterByCategoryComponent } from './components/search-result-page/filter-by-category/filter-by-category.component';
import { DiseaseAtAGlanceSnippetsComponent } from './components/disease/disease-page/disease-at-a-glance-snippets/disease-at-a-glance-snippets.component';
import { DiagnosticJourneyComponent } from './components/disease/disease-page/diagnostic-journey/diagnostic-journey.component';
import { ParticipatingInClinicalStudiesComponent } from './components/disease/disease-page/participating-in-clinical-studies/participating-in-clinical-studies.component';
import { RareDiseaseExpertComponent } from './components/disease/disease-page/rare-disease-expert/rare-disease-expert.component';
import { ClinicalStudiesComponent } from './components/disease/disease-page/clinical-studies/clinical-studies.component';
import { ClinicalTrialsContactUsComponent } from './components/disease/disease-page/clinical-trials-contact-us/clinical-trials-contact-us.component';
import { AdvocacyAndSupportGroupsComponent } from './components/disease/disease-page/advocacy-and-support-groups/advocacy-and-support-groups.component';
import { HttpErrorInterceptor } from './http-interceptors/error-interceptor';
import { InheritanceComponent } from './components/disease/disease-page/inheritance/inheritance.component';
import { ImprovingAccessComponent } from './components/pages/about/improving-access/improving-access.component';
import { ButtonDetailListSectionComponent } from './components/common/button-detail-list-section/button-detail-list-section.component';
import { ButtonDetailListLargerComponent } from './components/common/button-detail-list-larger/button-detail-list-larger.component';
import { GardIdComponent } from './redirects/gard-id/gard-id.component';
import { DiseaseSectionNavComponent } from './components/common/disease-section-nav/disease-section-nav.component';

import { RedirectParentComponent } from './components/disease/redirect-parent/redirect-parent.component';

import { DiseaseMultidisciplinaryComponent } from './components/disease/disease-page/disease-multidisciplinary/disease-multidisciplinary.component';

import { BannerRareDiseaseDayComponent } from './components/common/banner-rare-disease-day/banner-rare-disease-day.component';
import { BannerShutdownComponent } from './components/common/banner-shutdown/banner-shutdown.component';
import { BannerUnderReviewComponent } from './components/common/banner-under-review/banner-under-review.component';
import { SupportCenterRedirectComponent } from './redirects/support-center/support-center-redirect.component';

export const imports = [
    HttpClientModule,
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    NgbTooltipModule,
    NgbModalModule,
    NgbAccordionModule,
    NgbNavModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ScullyLibModule.forRoot({
        useTransferState: true,
        alwaysMonitor: true,
    }),
];

export const providers = [
    DecimalPipe,
    NumberOrStringToDecimalPipe,
    { provide: Window, useValue: window },
    TitleCaseWordPipe,
    NgbActiveModal,
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpErrorInterceptor,
        multi: true,
    },
];

@NgModule({
    imports,
    providers,
    bootstrap: [AppComponent],
    declarations: [
        AppComponent,
        SafeHTMLPipe,
        FooterComponent,
        SymptomsSectionComponent,
        DiseaseAboutComponent,
        AgeAtOnsetComponent,
        DiseaseAtAGlanceComponent,
        DiseaseAtAGlanceSummaryComponent,
        CausesComponent,
        CausesTabsComponent,
        ScrollToTopComponent,
        PatientOrganizationsAccountsComponent,
        NumberRoundingSuffixPipe,
        NumberOrStringToDecimalPipe,
        YourMedicalTeamComponent,
        TooltipComponent,
        EspanolDiseaseDetailComponent,
        SearchResultPageComponent,
        NotFoundComponent,
        ExternalLinkDirective,
        EmbeddedHtmlDirective,
        HomepageComponent,
        H3UnderstandComponent,
        H2AboutGardComponent,
        H5ContactUsComponent,
        ContactUsComponent,
        AboutComponent,
        GenericPageComponent,
        Co2ContactCenterComponent,
        ErrorListComponent,
        AboutDescriptionComponent,
        Ab4PoliciesComponent,
        ContactResourcesComponent,
        TitleCaseWordPipe,
        TopNavigationComponent,
        MainBannerComponent,
        MobileSearchItemComponent,
        MobilePanelListComponent,
        ViewSourcesReferencesComponent,
        BottomSourcesDateInformationComponent,
        MatchHeightDirective,
        ClampComponent,
        ModalBaseComponent,
        ContactUsEsComponent,
        BannerFeedbackComponent,
        DiseaseCasePipe,
        AutoFocusDirective,
        FilterByLetterComponent,
        FilterBubblesComponent,
        FilterByCategoryComponent,
        DiseaseAtAGlanceSnippetsComponent,
        DiagnosticJourneyComponent,
        ParticipatingInClinicalStudiesComponent,
        RareDiseaseExpertComponent,
        ClinicalStudiesComponent,
        ClinicalTrialsContactUsComponent,
        AdvocacyAndSupportGroupsComponent,
        InheritanceComponent,
        ImprovingAccessComponent,
        ButtonDetailListSectionComponent,
        ButtonDetailListLargerComponent,
        GardIdComponent,
        DiseaseSectionNavComponent,
        RedirectParentComponent,
        DiseaseMultidisciplinaryComponent,
        BannerRareDiseaseDayComponent,
        BannerShutdownComponent,
        BannerUnderReviewComponent,
        SupportCenterRedirectComponent,
    ],
})
export class AppModule {}
